import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import competitionService from '../services/competitionService';
import { useAuth } from '../context/AuthContext';
import MediaSourceInputRow from '../components/MediaSourceInputRow';
import './CreateCompetitionPage.css';

const DEFAULT_COMPETITION_DESCRIPTION = `Grow your channel, reach new audiences, and boost your video performance by participating in this creator challenge.

This competition is designed to help you:

- Increase engagement on your videos
- Reach new viewers through shared momentum
- Experiment with content and improve performance
- Compete fairly, regardless of your current subscriber count

Whether you are a new creator or an established one, this challenge gives you a real opportunity to grow.

Join this exciting creator challenge and showcase your creativity by building engaging content using the provided media sources.

In this competition, you are required to create a video using any of the given sound or video references (such as full song, short clips, or audio). You can choose the format and style that best fits your creativity - storytelling, devotional expression, reels, or any engaging concept.

How to Participate:
- Select any of the provided media sources
- Create your own video using the selected sound
- Add the required hashtag in your video description
- Upload your video on the supported platform
- Submit your video link on this platform

Tips for Better Performance:
- Focus on creativity and emotional connection
- Try different variations (multiple submissions allowed)
- Engage your audience to increase likes, comments, and shares
- Maintain good video and audio quality

Fair Evaluation System (Important)

Winners are not decided purely based on views, likes, or comments.

Our system evaluates submissions using a balanced approach that considers:

- Engagement (likes, comments, interaction)
- Performance relative to your channel size (subscriber count)
- Growth and traction of your video
- Creativity and originality

This means:

- New and small creators have a real chance to win
- High-quality content can outperform large channels
- Bigger creators may have some advantage, but not dominance

Our goal is to ensure a fair and level playing field where talent and creativity matter more than just audience size.

Evaluation Process:
- Top-performing entries will be shortlisted using system-based scoring
- Final winners will be selected through a combination of algorithmic scoring and manual judging

Important Notes:
- You can submit multiple videos, but only your best-performing submission will be considered for final evaluation
- Ensure your video includes the required hashtag and follows all rules
- Invalid or incomplete submissions may be disqualified`;

const CreateCompetitionPage = () => {
  const navigate = useNavigate();
  const { id: competitionId } = useParams();
  const { user, isAdmin } = useAuth();
  const isEditMode = Boolean(competitionId);

  const [form, setForm] = useState({
    name: '',
    description: DEFAULT_COMPETITION_DESCRIPTION,
    startDate: '',
    endDate: '',
    requiredHashtags: [],
    allowedSubmissionTypes: ['full', 'short'],
    rules: [],
    shortlistSize: 20,
    visibilityMode: 'limited',
    maxParticipants: '',
    registrationDeadline: '',
    bannerImage: '',
    prizes: [createDefaultPrize('winner')],
    mediaSources: [createDefaultMediaSource()],
    primarySourceIndex: 0,
    category: 'video',
    status: 'draft'
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState('');
  const [originalStatus, setOriginalStatus] = useState('draft');
  const [initialStartDate, setInitialStartDate] = useState('');
  const [hashtagInput, setHashtagInput] = useState('');
  const [validationErrors, setValidationErrors] = useState({ form: {}, mediaSources: [] });

  const isActiveCompetition = isEditMode && originalStatus === 'active';
  const isJudgingCompetition = isEditMode && originalStatus === 'judging';
  const canAdminTransitionLifecycle =
    isEditMode &&
    typeof isAdmin === 'function' &&
    isAdmin() &&
    (originalStatus === 'active' || originalStatus === 'judging');
  const todayDate = getCurrentDateOnly();
  const minStartDateTime = getMinStartDateTime({
    isEditMode,
    currentStartDate: form.startDate,
    initialStartDate,
    todayDate
  });
  const minEndDateTime = `${extractDatePart(form.startDate) || todayDate}T00:00`;

  const loadCompetition = useCallback(async () => {
    if (!isEditMode) {
      return;
    }

    try {
      setInitialLoading(true);
      setError('');
      const data = await competitionService.getById(competitionId);
      const competition = data.competition || data;

      if (competition.createdBy !== user?.id && !(typeof isAdmin === 'function' && isAdmin())) {
        navigate(`/competitions/${competitionId}`, { replace: true });
        return;
      }

      setForm({
        name: competition.name || '',
        description: competition.description || '',
        startDate: toDateTimeLocal(competition.startDate),
        endDate: toDateTimeLocal(competition.endDate),
        requiredHashtags: parseHashtags(competition.requiredHashtag),
        allowedSubmissionTypes: getAllowedSubmissionTypesFromCompetition(competition),
        rules: Array.isArray(competition.rules) ? competition.rules.filter((r) => r && typeof r === 'string') : [],
        shortlistSize: competition.shortlistSize || 20,
        visibilityMode: competition.visibilityMode || 'limited',
        maxParticipants: competition.maxParticipants || '',
        registrationDeadline: toDateTimeLocal(competition.registrationDeadline),
        bannerImage: competition.bannerImage || '',
        prizes: parsePrizesForEdit(competition.prizes),
        mediaSources: getEditableMediaSources(competition),
        primarySourceIndex: getSafePrimaryIndex(competition.primarySourceIndex, getEditableMediaSources(competition).length),
        category: competition.category || 'video',
        status: competition.status || 'draft'
      });
      setInitialStartDate(toDateTimeLocal(competition.startDate));
      setOriginalStatus(competition.status || 'draft');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load competition for editing');
    } finally {
      setInitialLoading(false);
    }
  }, [competitionId, isEditMode, isAdmin, navigate, user?.id]);

  useEffect(() => {
    loadCompetition();
  }, [loadCompetition]);

  const onChange = (e) => {
    if (isActiveCompetition && ['name', 'description', 'startDate', 'status', 'category', 'shortlistSize', 'visibilityMode', 'maxParticipants', 'bannerImage'].includes(e.target.name)) {
      return;
    }

    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setValidationErrors((prev) => ({ ...prev, form: { ...prev.form, [name]: undefined } }));
  };

  const addHashtag = (rawTag) => {
    const normalizedTag = normalizeHashtag(rawTag);
    if (!normalizedTag) {
      return;
    }

    setForm((prev) => {
      if (prev.requiredHashtags.includes(normalizedTag)) {
        return prev;
      }

      return {
        ...prev,
        requiredHashtags: [...prev.requiredHashtags, normalizedTag]
      };
    });

    setValidationErrors((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        requiredHashtags: undefined
      }
    }));
  };

  const removeHashtag = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      requiredHashtags: prev.requiredHashtags.filter((_, index) => index !== indexToRemove)
    }));
  };

  const onHashtagKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addHashtag(hashtagInput);
      setHashtagInput('');
      return;
    }

    if (event.key === 'Backspace' && !hashtagInput && form.requiredHashtags.length > 0) {
      event.preventDefault();
      removeHashtag(form.requiredHashtags.length - 1);
    }
  };

  const onHashtagInputChange = (event) => {
    setHashtagInput(event.target.value);
  };

  const onHashtagInputBlur = () => {
    if (!hashtagInput.trim()) {
      return;
    }

    addHashtag(hashtagInput);
    setHashtagInput('');
  };

  const onMediaSourceChange = (index, key, value) => {
    setForm((prev) => {
      const nextSources = [...prev.mediaSources];
      nextSources[index] = {
        ...nextSources[index],
        [key]: value
      };

      return {
        ...prev,
        mediaSources: nextSources
      };
    });

    setValidationErrors((prev) => {
      const nextMediaErrors = [...prev.mediaSources];
      nextMediaErrors[index] = {
        ...(nextMediaErrors[index] || {}),
        [key]: undefined
      };
      return {
        ...prev,
        mediaSources: nextMediaErrors
      };
    });
  };

  const onAddMediaSource = () => {
    setForm((prev) => ({
      ...prev,
      mediaSources: [...prev.mediaSources, createDefaultMediaSource()]
    }));
  };

  const onRemoveMediaSource = (indexToRemove) => {
    if (isActiveCompetition) {
      return;
    }

    setForm((prev) => {
      if (prev.mediaSources.length <= 1) {
        return prev;
      }

      const nextSources = prev.mediaSources.filter((_, index) => index !== indexToRemove);
      let nextPrimaryIndex = prev.primarySourceIndex;

      if (indexToRemove === prev.primarySourceIndex) {
        nextPrimaryIndex = 0;
      } else if (indexToRemove < prev.primarySourceIndex) {
        nextPrimaryIndex = prev.primarySourceIndex - 1;
      }

      return {
        ...prev,
        mediaSources: nextSources,
        primarySourceIndex: nextPrimaryIndex
      };
    });

    setValidationErrors((prev) => ({
      ...prev,
      mediaSources: prev.mediaSources.filter((_, index) => index !== indexToRemove)
    }));
  };

  const onSetPrimary = (index) => {
    setForm((prev) => ({
      ...prev,
      primarySourceIndex: index
    }));
  };

  const onToggleAllowedSubmissionType = (type) => {
    setForm((prev) => {
      const currentTypes = Array.isArray(prev.allowedSubmissionTypes) ? prev.allowedSubmissionTypes : [];
      const exists = currentTypes.includes(type);

      const nextTypes = exists
        ? currentTypes.filter((item) => item !== type)
        : [...currentTypes, type];

      return {
        ...prev,
        allowedSubmissionTypes: nextTypes
      };
    });

    setValidationErrors((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        allowedSubmissionTypes: undefined
      }
    }));
  };

  const onPrizeChange = (index, key, value) => {
    setForm((prev) => {
      const nextPrizes = [...prev.prizes];
      nextPrizes[index] = {
        ...nextPrizes[index],
        [key]: value
      };

      return {
        ...prev,
        prizes: nextPrizes
      };
    });

    setValidationErrors((prev) => {
      const nextPrizeErrors = [...(prev.prizes || [])];
      nextPrizeErrors[index] = {
        ...(nextPrizeErrors[index] || {}),
        [key]: undefined
      };

      return {
        ...prev,
        prizes: nextPrizeErrors,
        form: {
          ...prev.form,
          prizes: undefined
        }
      };
    });
  };

  const onAddPrize = (tier) => {
    setForm((prev) => ({
      ...prev,
      prizes: [...prev.prizes, createDefaultPrize(tier)]
    }));
  };

  const onRemovePrize = (indexToRemove) => {
    setForm((prev) => {
      if (!Array.isArray(prev.prizes) || prev.prizes.length <= 1) {
        return prev;
      }

      return {
        ...prev,
        prizes: prev.prizes.filter((_, index) => index !== indexToRemove)
      };
    });

    setValidationErrors((prev) => ({
      ...prev,
      prizes: (prev.prizes || []).filter((_, index) => index !== indexToRemove)
    }));
  };

  const onRuleChange = (index, value) => {
    setForm((prev) => {
      const nextRules = [...(prev.rules || [])];
      nextRules[index] = value;
      return { ...prev, rules: nextRules };
    });
  };

  const onAddRule = () => {
    setForm((prev) => ({
      ...prev,
      rules: [...(prev.rules || []), '']
    }));
  };

  const onRemoveRule = (index) => {
    setForm((prev) => ({
      ...prev,
      rules: (prev.rules || []).filter((_, i) => i !== index)
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const nextValidationErrors = validateCompetitionForm({
      form,
      todayDate,
      isEditMode,
      initialStartDate
    });
    setValidationErrors(nextValidationErrors);

    if (hasValidationIssues(nextValidationErrors)) {
      setError('Please fix highlighted fields before submitting.');
      return;
    }

    if (!user?.id) {
      setError('User session not found. Please login again.');
      return;
    }

    try {
      setLoading(true);
      const basePayload = {
        name: form.name.trim(),
        description: form.description.trim(),
        startDate: new Date(form.startDate).getTime(),
        endDate: new Date(form.endDate).getTime(),
        requiredHashtag: joinHashtags(form.requiredHashtags),
        allowedSubmissionTypes: Array.from(new Set((form.allowedSubmissionTypes || []).filter(isSupportedSubmissionType))),
        prizes: (form.prizes || [])
          .map(function(prize) {
            return {
              title: (prize.title || '').trim(),
              cost: Number(prize.cost || 0),
              description: (prize.description || '').trim(),
              tier: prize.tier === 'consolation' ? 'consolation' : 'winner'
            };
          })
          .filter(function(prize) {
            return prize.title && prize.cost > 0;
          }),
        mediaSources: form.mediaSources.map((source) => ({
          platform: source.platform,
          url: source.url.trim(),
          type: source.type
        })),
        primarySourceIndex: form.primarySourceIndex,
        category: form.category,
        status: form.status,
        rules: (form.rules || []).map((r) => r.trim()).filter(Boolean),
        shortlistSize: Math.max(1, Number(form.shortlistSize) || 20),
        visibilityMode: form.visibilityMode || 'limited',
        maxParticipants: form.maxParticipants ? Number(form.maxParticipants) : null,
        registrationDeadline: form.registrationDeadline ? new Date(form.registrationDeadline).getTime() : null,
        bannerImage: form.bannerImage ? form.bannerImage.trim() : null,
        platform: form.mediaSources[form.primarySourceIndex]?.platform || 'youtube',
        createdBy: user.id
      };

      const payload = isEditMode && isActiveCompetition
        ? {
            endDate: basePayload.endDate,
            requiredHashtag: basePayload.requiredHashtag,
            allowedSubmissionTypes: basePayload.allowedSubmissionTypes,
            prizes: basePayload.prizes,
            registrationDeadline: basePayload.registrationDeadline,
            bannerImage: basePayload.bannerImage,
            mediaSources: basePayload.mediaSources,
            primarySourceIndex: basePayload.primarySourceIndex
          }
        : basePayload;

      const result = isEditMode
        ? await competitionService.update(competitionId, payload)
        : await competitionService.create(payload);
      const createdCompetition = result?.competition;

      if (createdCompetition?.id) {
        navigate(`/competitions/${createdCompetition.id}`);
        return;
      }

      navigate('/competitions');
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to create competition');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="competition-list-page create-competition-page">
      <div className="competition-content" style={{ maxWidth: 900, margin: '0 auto' }}>
        <div className="competition-header">
          <div>
            <h1>{isEditMode ? 'Edit Competition' : 'Create Competition'}</h1>
            <p>
              {isEditMode
                ? (isActiveCompetition
                    ? 'Active competitions allow end date, hashtag, and adding media sources.'
                    : 'Update competition details while in draft mode.')
                : 'Set up a new competition for creators.'}
            </p>
          </div>
        </div>

        {initialLoading && <p>Loading competition...</p>}

        <form onSubmit={onSubmit} className="dashboard-card" style={{ padding: 24 }}>
          {error && <p style={{ color: '#b42318', marginBottom: 12 }}>{error}</p>}

          <div className="form-group">
            <label>Name</label>
            <input name="name" value={form.name} onChange={onChange} required disabled={isActiveCompetition} />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea name="description" value={form.description} onChange={onChange} rows={4} required disabled={isActiveCompetition} />
          </div>

          <div className="form-group">
            <label>Start Date</label>
            <input
              type="datetime-local"
              name="startDate"
              value={form.startDate}
              onChange={onChange}
              required
              disabled={isActiveCompetition}
              min={minStartDateTime}
            />
            {validationErrors.form.startDate && <small style={{ color: '#b42318' }}>{validationErrors.form.startDate}</small>}
          </div>

          <div className="form-group">
            <label>End Date</label>
            <input
              type="datetime-local"
              name="endDate"
              value={form.endDate}
              onChange={onChange}
              required
              min={minEndDateTime}
            />
            {validationErrors.form.endDate && <small style={{ color: '#b42318' }}>{validationErrors.form.endDate}</small>}
          </div>

          <CollapsibleSection title="Media Sources">
            <p style={{ margin: '0 0 14px 0', color: '#475467', fontSize: 14 }}>
              Add videos or audio for creators to use. You can add multiple sources like full song, shorts, or audio.
            </p>

            {form.mediaSources.map((source, index) => (
              <MediaSourceInputRow
                key={`media-source-${index}`}
                data={source}
                index={index}
                onChange={onMediaSourceChange}
                onRemove={onRemoveMediaSource}
                onSetPrimary={onSetPrimary}
                isPrimary={form.primarySourceIndex === index}
                canRemove={form.mediaSources.length > 1 && !isActiveCompetition}
                errors={validationErrors.mediaSources[index]}
              />
            ))}

            <button type="button" className="action-button" onClick={onAddMediaSource} style={{ width: 'auto' }}>
              Add Media Source
            </button>
          </CollapsibleSection>

          <CollapsibleSection title="Accepted Submission Types">
            <p style={{ margin: '0 0 14px 0', color: '#475467', fontSize: 14 }}>
              Choose which submission formats creators can use while submitting entries.
            </p>
            <div className="submission-type-chip-group">
              {SUBMISSION_TYPE_OPTIONS.map((option) => {
                const checked = form.allowedSubmissionTypes.includes(option.value);
                return (
                  <label key={option.value} className={`submission-type-chip ${checked ? 'active' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggleAllowedSubmissionType(option.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                );
              })}
            </div>
            {validationErrors.form.allowedSubmissionTypes && <small style={{ color: '#b42318' }}>{validationErrors.form.allowedSubmissionTypes}</small>}
          </CollapsibleSection>

          <CollapsibleSection title="Prizes">
            <p style={{ margin: '0 0 14px 0', color: '#475467', fontSize: 14 }}>
              Add winner and optional consolation prizes. Each prize should include title and cost.
            </p>

            {form.prizes.map((prize, index) => (
              <div key={`prize-${index}`} className="prize-row">
                <div className="prize-row-header">
                  <strong>{prize.tier === 'consolation' ? 'Consolation Prize' : 'Winner Prize'} {index + 1}</strong>
                  <button
                    type="button"
                    className="action-button"
                    onClick={() => onRemovePrize(index)}
                    disabled={form.prizes.length <= 1}
                    style={{ width: 'auto', padding: '8px 12px' }}
                  >
                    Remove
                  </button>
                </div>

                <div className="prize-grid">
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Type</label>
                    <select
                      value={prize.tier}
                      onChange={(event) => onPrizeChange(index, 'tier', event.target.value)}
                    >
                      <option value="winner">Winner</option>
                      <option value="consolation">Consolation</option>
                    </select>
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Title</label>
                    <input
                      value={prize.title}
                      onChange={(event) => onPrizeChange(index, 'title', event.target.value)}
                      placeholder="e.g. 1st Prize, Gift Hamper, Course Access"
                    />
                    {validationErrors.prizes?.[index]?.title && <small style={{ color: '#b42318' }}>{validationErrors.prizes[index].title}</small>}
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Cost / Value</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={prize.cost}
                      onChange={(event) => onPrizeChange(index, 'cost', event.target.value)}
                      placeholder="e.g. 5000"
                    />
                    {validationErrors.prizes?.[index]?.cost && <small style={{ color: '#b42318' }}>{validationErrors.prizes[index].cost}</small>}
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Description (optional)</label>
                    <textarea
                      rows={2}
                      value={prize.description}
                      onChange={(event) => onPrizeChange(index, 'description', event.target.value)}
                      placeholder="Extra details for this prize"
                    />
                  </div>
                </div>
              </div>
            ))}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button type="button" className="action-button" onClick={() => onAddPrize('winner')} style={{ width: 'auto' }}>
                Add Winner Prize
              </button>
              <button type="button" className="action-button" onClick={() => onAddPrize('consolation')} style={{ width: 'auto' }}>
                Add Consolation Prize
              </button>
            </div>
            {validationErrors.form.prizes && <small style={{ color: '#b42318' }}>{validationErrors.form.prizes}</small>}
          </CollapsibleSection>

          <div className="form-group">
            <label>Required Hashtags</label>
            <div className="hashtag-chip-control">
              {form.requiredHashtags.map((tag, index) => (
                <span key={`${tag}-${index}`} className="hashtag-chip">
                  {tag}
                  <button
                    type="button"
                    className="remove-chip-button"
                    onClick={() => removeHashtag(index)}
                    disabled={isActiveCompetition && form.requiredHashtags.length === 1}
                    aria-label={`Remove ${tag}`}
                  >
                    x
                  </button>
                </span>
              ))}
              <input
                name="requiredHashtagsInput"
                value={hashtagInput}
                onChange={onHashtagInputChange}
                onKeyDown={onHashtagKeyDown}
                onBlur={onHashtagInputBlur}
                placeholder="#CreatorYatra"
                disabled={false}
              />
            </div>
            <small style={{ color: '#475467' }}>Press Enter or comma to add a hashtag.</small>
            {validationErrors.form.requiredHashtags && <small style={{ color: '#b42318' }}>{validationErrors.form.requiredHashtags}</small>}
          </div>

          <CollapsibleSection title="Rules & Guidelines" isOptional defaultOpen={false}>
            <p style={{ margin: '0 0 14px 0', color: '#475467', fontSize: 14 }}>
              Optional rules shown to participants. Each entry appears as a separate bullet.
            </p>
            {form.rules.map((rule, index) => (
              <div key={`rule-${index}`} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <input
                  value={rule}
                  onChange={(event) => onRuleChange(index, event.target.value)}
                  placeholder={`Rule ${index + 1}, e.g. You must use the required hashtag`}
                  disabled={isActiveCompetition}
                  style={{ flex: 1, padding: '12px 14px', border: '1px solid #d0d5dd', borderRadius: 10, fontSize: 14, color: '#101828', background: '#ffffff' }}
                />
                <button
                  type="button"
                  className="action-button"
                  onClick={() => onRemoveRule(index)}
                  disabled={isActiveCompetition}
                  style={{ width: 'auto', padding: '10px 14px', flexShrink: 0 }}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="action-button"
              onClick={onAddRule}
              disabled={isActiveCompetition}
              style={{ width: 'auto' }}
            >
              Add Rule
            </button>
          </CollapsibleSection>

          <CollapsibleSection title="Advanced Settings" isOptional defaultOpen={false}>
            <p style={{ margin: '0 0 14px 0', color: '#475467', fontSize: 14 }}>
              Optional settings for participation limits, submission visibility, and branding.
            </p>
            <div className="prize-grid">
              <div className="form-group" style={{ margin: 0 }}>
                <label>Shortlist Size</label>
                <input
                  type="number"
                  name="shortlistSize"
                  min="1"
                  value={form.shortlistSize}
                  onChange={onChange}
                  disabled={isActiveCompetition}
                  placeholder="20"
                />
                <small style={{ color: '#475467' }}>Top N videos moved to judging.</small>
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label>Submission Visibility</label>
                <select
                  name="visibilityMode"
                  value={form.visibilityMode}
                  onChange={onChange}
                  disabled={isActiveCompetition}
                >
                  <option value="limited">Limited (first few visible)</option>
                  <option value="public">Public (all visible during active)</option>
                  <option value="hidden">Hidden (none visible until judging)</option>
                </select>
                <small style={{ color: '#475467' }}>Controls what participants see during the active phase.</small>
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label>Max Participants (optional)</label>
                <input
                  type="number"
                  name="maxParticipants"
                  min="1"
                  value={form.maxParticipants}
                  onChange={onChange}
                  disabled={isActiveCompetition}
                  placeholder="Unlimited"
                />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label>Registration Deadline (optional)</label>
                <input
                  type="datetime-local"
                  name="registrationDeadline"
                  value={form.registrationDeadline}
                  onChange={onChange}
                  min={minEndDateTime}
                />
                <small style={{ color: '#475467' }}>Last date creators can join.</small>
              </div>

              <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                <label>Banner Image URL (optional)</label>
                <input
                  name="bannerImage"
                  value={form.bannerImage}
                  onChange={onChange}
                  disabled={isActiveCompetition}
                  placeholder="https://example.com/banner.jpg"
                />
                <small style={{ color: '#475467' }}>Shown on competition cards and the detail page.</small>
              </div>
            </div>
          </CollapsibleSection>

          <div className="form-group">
            <label>Status</label>
            <select
              name="status"
              value={form.status}
              onChange={onChange}
              disabled={(isActiveCompetition || isJudgingCompetition) && !canAdminTransitionLifecycle}
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              {(originalStatus === 'active' || originalStatus === 'judging' || !isEditMode) && (
                <option value="judging">Judging</option>
              )}
              {(!isEditMode || originalStatus === 'judging' || (!isActiveCompetition && !isJudgingCompetition)) && (
                <option value="completed">Completed</option>
              )}
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button
              type="submit"
              className="action-button primary"
              disabled={loading || !isFormSubmittable({ form, todayDate, isEditMode, initialStartDate })}
            >
              {loading ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Competition')}
            </button>
            <button type="button" className="action-button" onClick={() => navigate('/dashboard')}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

function createDefaultMediaSource() {
  return {
    platform: 'youtube',
    url: '',
    type: 'full'
  };
}

function createDefaultPrize(tier) {
  return {
    title: '',
    cost: '',
    description: '',
    tier: tier === 'consolation' ? 'consolation' : 'winner'
  };
}

const SUBMISSION_TYPE_OPTIONS = [
  { value: 'short', label: 'Short Video' },
  { value: 'full', label: 'Full Video' },
  { value: 'audio', label: 'Audio-based' }
];

function isSupportedSubmissionType(type) {
  return type === 'short' || type === 'full' || type === 'audio';
}

function isValidYouTubeUrl(url) {
  return /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)[a-zA-Z0-9_-]{11}.*$/.test(url || '');
}

function validateCompetitionForm({ form, todayDate, isEditMode, initialStartDate }) {
  const errors = {
    form: {},
    mediaSources: [],
    prizes: []
  };

  const hasChangedStartDate = !isEditMode || extractDatePart(form.startDate) !== extractDatePart(initialStartDate);
  const startDatePart = extractDatePart(form.startDate);
  const endDatePart = extractDatePart(form.endDate);

  if (startDatePart && startDatePart < todayDate && hasChangedStartDate) {
    errors.form.startDate = 'Start date cannot be in the past';
  }

  if (startDatePart && endDatePart && endDatePart < startDatePart) {
    errors.form.endDate = 'End date cannot be before start date';
  }

  if (!Array.isArray(form.requiredHashtags) || form.requiredHashtags.length === 0) {
    errors.form.requiredHashtags = 'At least one hashtag is required';
  }

  const allowedSubmissionTypes = Array.isArray(form.allowedSubmissionTypes)
    ? form.allowedSubmissionTypes.filter(isSupportedSubmissionType)
    : [];

  if (allowedSubmissionTypes.length === 0) {
    errors.form.allowedSubmissionTypes = 'Select at least one accepted submission type';
  }

  if (!Array.isArray(form.mediaSources) || form.mediaSources.length === 0) {
    errors.form.mediaSources = 'At least one media source is required.';
    return errors;
  }

  form.mediaSources.forEach((source, index) => {
    const sourceErrors = {};

    if (!source.platform) {
      sourceErrors.platform = 'Platform is required';
    }

    if (!source.url || !source.url.trim()) {
      sourceErrors.url = 'URL is required';
    } else if (source.platform === 'youtube' && !isValidYouTubeUrl(source.url.trim())) {
      sourceErrors.url = 'Invalid YouTube URL';
    }

    errors.mediaSources[index] = sourceErrors;
  });

  const prizes = Array.isArray(form.prizes) ? form.prizes : [];
  if (prizes.length === 0) {
    errors.form.prizes = 'Add at least one winner prize';
  }

  prizes.forEach((prize, index) => {
    const prizeErrors = {};
    const title = (prize?.title || '').trim();
    const cost = Number(prize?.cost || 0);

    if (!title) {
      prizeErrors.title = 'Prize title is required';
    }

    if (!Number.isFinite(cost) || cost <= 0) {
      prizeErrors.cost = 'Prize cost/value must be greater than 0';
    }

    errors.prizes[index] = prizeErrors;
  });

  if (prizes.length > 0) {
    const hasWinnerPrize = prizes.some((prize) => (prize?.tier || 'winner') === 'winner');
    if (!hasWinnerPrize) {
      errors.form.prizes = 'At least one winner prize is required';
    }
  }

  return errors;
}

function hasValidationIssues(errors) {
  const formIssues = Object.values(errors.form || {}).some(Boolean);
  const mediaIssues = (errors.mediaSources || []).some((sourceErrors) => {
    return sourceErrors && Object.values(sourceErrors).some(Boolean);
  });

  return formIssues || mediaIssues;
}

function isFormSubmittable({ form, todayDate, isEditMode, initialStartDate }) {

  if (!form.name || !form.description || !form.startDate || !form.endDate) {
    return false;
  }

  if (!Array.isArray(form.requiredHashtags) || form.requiredHashtags.length === 0) {
    return false;
  }

  const allowedSubmissionTypes = Array.isArray(form.allowedSubmissionTypes)
    ? form.allowedSubmissionTypes.filter(isSupportedSubmissionType)
    : [];

  if (allowedSubmissionTypes.length === 0) {
    return false;
  }

  const hasChangedStartDate = !isEditMode || extractDatePart(form.startDate) !== extractDatePart(initialStartDate);
  const startDatePart = extractDatePart(form.startDate);
  const endDatePart = extractDatePart(form.endDate);

  if (startDatePart < todayDate && hasChangedStartDate) {
    return false;
  }

  if (endDatePart < startDatePart) {
    return false;
  }

  if (!Array.isArray(form.mediaSources) || form.mediaSources.length === 0) {
    return false;
  }

  if (!Array.isArray(form.prizes) || form.prizes.length === 0) {
    return false;
  }

  const hasWinnerPrize = form.prizes.some((prize) => (prize?.tier || 'winner') === 'winner');
  if (!hasWinnerPrize) {
    return false;
  }

  const arePrizesValid = form.prizes.every((prize) => {
    const title = (prize?.title || '').trim();
    const cost = Number(prize?.cost || 0);
    return title.length > 0 && Number.isFinite(cost) && cost > 0;
  });

  if (!arePrizesValid) {
    return false;
  }

  return form.mediaSources.every((source) => {
    if (!source.platform || !source.url || !source.url.trim()) {
      return false;
    }

    if (source.platform === 'youtube' && !isValidYouTubeUrl(source.url.trim())) {
      return false;
    }

    return true;
  });
}

function getCurrentDateOnly() {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getMinStartDateTime({ isEditMode, currentStartDate, initialStartDate, todayDate }) {
  const todayStart = `${todayDate}T00:00`;

  if (!isEditMode) {
    return todayStart;
  }

  const initialDatePart = extractDatePart(initialStartDate);
  if (initialDatePart && initialDatePart < todayDate) {
    return `${initialDatePart}T00:00`;
  }

  const currentDatePart = extractDatePart(currentStartDate);
  if (currentDatePart && currentDatePart < todayDate) {
    return `${currentDatePart}T00:00`;
  }

  return todayStart;
}

function extractDatePart(dateTimeString) {
  if (!dateTimeString || typeof dateTimeString !== 'string') {
    return '';
  }

  return dateTimeString.slice(0, 10);
}

function normalizeHashtag(rawTag) {
  if (!rawTag || typeof rawTag !== 'string') {
    return '';
  }

  const cleaned = rawTag.trim().replace(/,$/, '').replace(/\s+/g, '');
  if (!cleaned) {
    return '';
  }

  const hashtagBody = cleaned.startsWith('#') ? cleaned.slice(1) : cleaned;
  if (!hashtagBody) {
    return '';
  }

  return `#${hashtagBody}`;
}

function parseHashtags(value) {
  if (!value || typeof value !== 'string') {
    return [];
  }

  const parts = value.split(/[\s,]+/).filter(Boolean);
  const tags = parts
    .map(normalizeHashtag)
    .filter(Boolean);

  return Array.from(new Set(tags));
}

function joinHashtags(hashtags) {
  if (!Array.isArray(hashtags) || hashtags.length === 0) {
    return '';
  }

  return hashtags.join(' ');
}

function getEditableMediaSources(competition) {
  if (Array.isArray(competition.mediaSources) && competition.mediaSources.length > 0) {
    return competition.mediaSources.map(function(source) {
      return {
        platform: source.platform || 'youtube',
        url: source.url || '',
        type: source.type || 'full'
      };
    });
  }

  if (competition.youtubeLink) {
    return [
      {
        platform: 'youtube',
        url: competition.youtubeLink,
        type: 'full'
      }
    ];
  }

  return [createDefaultMediaSource()];
}

function getAllowedSubmissionTypesFromCompetition(competition) {
  const incoming = Array.isArray(competition?.allowedSubmissionTypes)
    ? competition.allowedSubmissionTypes
    : [];

  const normalizedIncoming = incoming.filter(isSupportedSubmissionType);
  if (normalizedIncoming.length > 0) {
    return Array.from(new Set(normalizedIncoming));
  }

  const fromSources = Array.isArray(competition?.mediaSources)
    ? competition.mediaSources
      .map((source) => source?.type)
      .filter(isSupportedSubmissionType)
    : [];

  if (fromSources.length > 0) {
    return Array.from(new Set(fromSources));
  }

  return ['full'];
}

function parsePrizesForEdit(prizes) {
  if (!Array.isArray(prizes) || prizes.length === 0) {
    return [createDefaultPrize('winner')];
  }

  const normalized = prizes.map((prize) => {
    if (typeof prize === 'string') {
      return {
        title: prize,
        cost: '',
        description: '',
        tier: 'winner'
      };
    }

    if (prize && typeof prize === 'object') {
      return {
        title: prize.title || prize.name || '',
        cost: prize.cost ?? prize.amount ?? '',
        description: prize.description || prize.value || '',
        tier: prize.tier === 'consolation' ? 'consolation' : 'winner'
      };
    }

    return createDefaultPrize('winner');
  });

  return normalized.length > 0 ? normalized : [createDefaultPrize('winner')];
}

function CollapsibleSection({ title, isOptional = false, defaultOpen = true, children }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div style={{ marginTop: 20, marginBottom: 20 }}>
      <button
        type="button"
        className="collapsible-section-header"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <h3 className="collapsible-section-title">
          {title}
          {isOptional && <span className="collapsible-optional-tag">(Optional)</span>}
        </h3>
        <svg
          className={`collapsible-chevron${isOpen ? ' open' : ''}`}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
        >
          <path d="M5 7.5L10 12.5L15 7.5" />
        </svg>
      </button>
      {isOpen && <div className="collapsible-section-body">{children}</div>}
    </div>
  );
}

function getSafePrimaryIndex(index, length) {
  if (!Number.isInteger(index) || index < 0 || index >= length) {
    return 0;
  }

  return index;
}

function toDateTimeLocal(timestamp) {
  if (!timestamp) {
    return '';
  }

  const date = new Date(timestamp);
  const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - timezoneOffsetMs).toISOString().slice(0, 16);
}

export default CreateCompetitionPage;
